import { div, h1, h2, p, span, button, img, fnstate } from '@srfnstack/fntags'
import { StarRating } from '../components/StarRating.js'
import { AiAppeals } from '../components/AiAppeals.js'

// Stage-1 gate: review the RAW generated artwork before any upscale/mockup credits
// are spent. Approve → the product resumes the pipeline; Reject → dropped.
export const ArtworkApproval = () => {
  const queue = fnstate([])
  const loading = fnstate(true)
  const busy = fnstate(null)
  const viewer = fnstate(null) // concept id being viewed full-screen, or null

  const load = async () => {
    loading(true)
    try {
      const res = await fetch('/api/system/autopilot/artwork-queue')
      queue(res.ok ? await res.json() : [])
    } catch { queue([]) }
    loading(false)
  }

  const act = async (id, action) => {
    busy(id)
    let ok = false
    try {
      const res = await fetch(`/api/system/autopilot/artwork-queue/${id}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}'
      })
      if (res.ok) {
        ok = true
        queue(queue().filter(x => x.id !== id))
      } else {
        const e = await res.json().catch(() => ({}))
        window.alert(`Failed to ${action}: ${e.error || 'unknown error'}`)
      }
    } catch (err) {
      window.alert(`Failed to ${action}: ${err.message}`)
    }
    busy(null)
    return ok
  }

  // Full-screen carousel: act on the current item, then advance to the one that
  // takes its place in the queue (or close when it was the last).
  const actAndAdvance = async (item, action) => {
    const idx = queue().findIndex(x => x.id === item.id)
    if (await act(item.id, action)) {
      const rest = queue()
      const next = rest[Math.min(idx, rest.length - 1)]
      viewer(next ? next.id : null)
    }
  }

  const step = (delta) => {
    const list = queue()
    if (list.length === 0) return
    const idx = list.findIndex(x => x.id === viewer())
    viewer(list[(idx + delta + list.length) % list.length].id)
  }

  const card = (item) => div({ class: 'card bg-base-200 border border-base-300 p-4' },
    item.artwork_path
      ? img({
        src: `/api/image?file=${encodeURIComponent(item.artwork_path)}`,
        class: 'w-full aspect-square object-contain rounded bg-base-100 mb-3 cursor-zoom-in',
        title: 'Click to view full screen',
        loading: 'lazy',
        decoding: 'async',
        onclick: () => viewer(item.id)
      })
      : div({ class: 'w-full aspect-square rounded bg-base-100 mb-3 flex items-center justify-center opacity-50' }, 'no image'),
    h2({ class: 'font-semibold text-sm mb-2' }, item.title || `Concept #${item.id}`),
    div({ class: 'flex flex-wrap items-center gap-2 mb-4 text-xs' },
      span({ class: 'badge badge-ghost badge-sm' }, item.product_type || 'product'),
      item.strategy ? span({ class: 'badge badge-outline badge-sm' }, item.strategy) : '',
      (item.quality_score !== null && item.quality_score !== undefined)
        ? span({ class: 'badge badge-success badge-sm' }, `art ${Number(item.quality_score).toFixed(1)}`)
        : ''
    ),
    // Target tribe + why they'd like it — so you can judge designs for niches you're not part of.
    item.target_tribe
      ? div({ class: 'mb-4 rounded-lg bg-base-100 p-3 text-xs' },
        div({ class: 'flex flex-wrap items-center gap-2 mb-1' },
          span({ class: 'uppercase opacity-50 font-bold tracking-wide' }, 'Tribe'),
          span({ class: 'font-semibold' }, item.target_tribe),
          (item.tribe_fit_score !== null && item.tribe_fit_score !== undefined)
            ? span({ class: `badge badge-sm ${item.tribe_fit_score >= 7 ? 'badge-success' : item.tribe_fit_score >= 4 ? 'badge-warning' : 'badge-error'}` }, `fit ${item.tribe_fit_score}/10`)
            : ''
        ),
        item.tribe_fit_reason ? div({ class: 'opacity-70 leading-snug' }, item.tribe_fit_reason) : ''
      )
      : '',
    item.asset_id
      ? div({ class: 'flex justify-center mb-3' },
        StarRating({ assetId: item.asset_id, initial: item.rating, onRated: r => { item.rating = r } })
      )
      : '',
    div({ class: 'flex gap-2' },
      button({
        class: 'btn btn-success flex-1',
        disabled: busy.bindAttr(b => b === item.id),
        onclick: () => act(item.id, 'approve')
      }, busy.bindAs(b => b === item.id ? span({ class: 'loading loading-spinner loading-sm' }) : 'Approve Artwork')),
      button({
        class: 'btn btn-error btn-outline',
        disabled: busy.bindAttr(b => b === item.id),
        onclick: () => act(item.id, 'reject')
      }, 'Reject')
    )
  )

  load()

  return div({ class: 'p-6' },
    div({ class: 'flex items-center gap-3 mb-1' },
      h1({ class: 'text-3xl font-bold' }, 'Artwork Approval'),
      button({ class: 'btn btn-sm btn-ghost', onclick: load }, '↻ Refresh')
    ),
    p({ class: 'text-sm opacity-70 mb-6' },
      'Stage 1 of 2: approve the raw artwork before any upscale + mockup credits are spent. Approved designs continue to mockups and then the publish queue; rejected ones are dropped.'),
    AiAppeals(),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : queue.bindAs(list => (list.length === 0)
        ? div({ class: 'text-center opacity-60 p-12' }, 'No artwork waiting for review.')
        : div({ class: 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl' }, list.map(card)))),
    // Full-screen review carousel — click any artwork to review the queue at
    // full size: arrows flip through, approve/reject auto-advance.
    viewer.bindAs(id => {
      if (!id) return span()
      const list = queue()
      const idx = list.findIndex(x => x.id === id)
      const item = list[idx]
      if (!item) return span()
      return div({
        class: 'fixed inset-0 z-[100] bg-black/90 flex flex-col p-4 cursor-zoom-out',
        onclick: () => viewer(null)
      },
      div({ class: 'flex-1 min-h-0 flex items-center justify-center' },
        img({
          src: `/api/image?file=${encodeURIComponent(item.artwork_path)}`,
          class: 'max-w-full max-h-full object-contain rounded shadow-2xl cursor-default',
          onclick: (e) => e.stopPropagation()
        })
      ),
      // Bottom bar: title, stars, approve/reject.
      div({
        class: 'flex flex-wrap items-center justify-center gap-x-6 gap-y-3 pt-4 cursor-default',
        onclick: (e) => e.stopPropagation()
      },
      div({ class: 'text-white/80 text-sm max-w-md truncate' }, item.title || `Concept #${item.id}`),
      item.asset_id ? StarRating({ assetId: item.asset_id, initial: item.rating, onRated: r => { item.rating = r } }) : '',
      div({ class: 'flex gap-2' },
        button({
          class: 'btn btn-success',
          disabled: busy.bindAttr(b => b === item.id),
          onclick: () => actAndAdvance(item, 'approve')
        }, busy.bindAs(b => b === item.id ? span({ class: 'loading loading-spinner loading-sm' }) : 'Approve Artwork')),
        button({
          class: 'btn btn-error btn-outline',
          disabled: busy.bindAttr(b => b === item.id),
          onclick: () => actAndAdvance(item, 'reject')
        }, 'Reject')
      )
      ),
      button({
        class: 'btn btn-circle btn-lg absolute left-4 top-1/2 -translate-y-1/2',
        onclick: (e) => { e.stopPropagation(); step(-1) }
      }, span({ class: 'material-symbols-rounded' }, 'chevron_left')),
      button({
        class: 'btn btn-circle btn-lg absolute right-4 top-1/2 -translate-y-1/2',
        onclick: (e) => { e.stopPropagation(); step(1) }
      }, span({ class: 'material-symbols-rounded' }, 'chevron_right')),
      span({ class: 'absolute top-4 left-4 badge badge-neutral' }, `${idx + 1} / ${list.length}`),
      button({ class: 'btn btn-circle btn-sm absolute top-4 right-4', onclick: (e) => { e.stopPropagation(); viewer(null) } },
        span({ class: 'material-symbols-rounded' }, 'close'))
      )
    })
  )
}
